"use client"

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Copy, Edit } from 'lucide-react'
import { api } from '@/lib/api'
import { useToast } from '@/components/ui/use-toast'

const enrichmentOptions = [
  "Recent Related News",
  "Related Statistics",
  "Current Trends",
  "Examples & Case Studies",
  "Common Misconceptions",
  "Popular Discussions",
  "Explain the Concept",
  "Practical Applications",
  "Step-by-Step Process",
  "Future Implications",
  "Debunking Myths",
  "Related Tools and Resources",
  "Recent Studies",
  "Actionable Tips"
]

interface ContentEnrichmentProps {
  onVerify: (contentId: string) => void
  contentId: string
}

export function ContentEnrichment({ onVerify, contentId }: ContentEnrichmentProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [enrichedContent, setEnrichedContent] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isEnriching, setIsEnriching] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    console.log('ContentEnrichment mounted with contentId:', contentId);
  }, [contentId]);

  const handleEnrichment = async () => {
    if (selectedOptions.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select at least one enrichment option",
      });
      return;
    }
    
    setIsEnriching(true);
    try {
      console.log('Executing enrichment with:', { contentId, selectedOptions });
      const response = await api.executeEnrichment(contentId, selectedOptions);
      
      if (response.success && response.enrichedContent) {
        setEnrichedContent(response.enrichedContent.content);
        
        // Log metadata if available
        if (response.enrichedContent.metadata) {
          console.log('Enrichment metadata:', {
            topics: response.enrichedContent.metadata.topics,
            sources: response.enrichedContent.metadata.sources,
            timestamp: response.enrichedContent.metadata.timestamp,
            hasUpdates: response.enrichedContent.metadata.hasUpdates
          });
        }

        toast({
          title: "Content enriched successfully",
          description: `Content has been enriched with ${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''}.${
            response.enrichedContent.metadata?.sources ? 
            ` Using ${response.enrichedContent.metadata.sources.length} sources.` : ''
          }`,
        });

        // Call onVerify with the enriched content ID
        onVerify(response.enrichedContent.content_id);
      } else {
        throw new Error('Enrichment response was not successful');
      }
    } catch (error) {
      console.error('Enrichment error:', error);
      toast({
        variant: "destructive",
        title: "Error enriching content",
        description: error instanceof Error ? error.message : "An error occurred while enriching your content.",
      });
    } finally {
      setIsEnriching(false);
    }
  }

  const toggleOption = (option: string) => {
    setSelectedOptions(prev => {
      if (prev.includes(option)) {
        return prev.filter(o => o !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(enrichedContent);
      toast({
        title: "Copied to clipboard",
        description: "The enriched content has been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Could not copy the content to clipboard.",
      });
    }
  };

  return (
    <Tabs defaultValue="options" className="w-full">
      <TabsList className="inline-flex justify-start">
        <TabsTrigger value="options">Enrichment Options</TabsTrigger>
        <TabsTrigger value="result" disabled={!enrichedContent}>Result</TabsTrigger>
      </TabsList>

      <TabsContent value="options">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {enrichmentOptions.map((option) => (
            <Button
              key={option}
              variant={selectedOptions.includes(option) ? "default" : "outline"}
              onClick={() => toggleOption(option)}
              className="justify-start text-sm"
              size="sm"
            >
              {option}
            </Button>
          ))}
        </div>
        <Button 
          className="w-full mt-4" 
          disabled={selectedOptions.length === 0 || isEnriching}
          onClick={handleEnrichment}
        >
          {isEnriching ? 'Enriching...' : `Execute Enrichment (${selectedOptions.length} selected)`}
        </Button>
      </TabsContent>

      <TabsContent value="result">
        {isEditing ? (
          <div className="space-y-4">
            <Textarea 
              className="min-h-[200px]"
              value={enrichedContent}
              onChange={(e) => setEnrichedContent(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-muted p-4 rounded-md min-h-[200px] relative">
            <p className="text-muted-foreground whitespace-pre-wrap">
              {enrichedContent || "Enriched content will appear here..."}
            </p>
            <div className="absolute top-2 right-2 flex gap-2">
              <Button variant="ghost" size="icon" onClick={handleCopy}>
                <Copy size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
                <Edit size={16} />
              </Button>
            </div>
          </div>
        )}
      </TabsContent>
    </Tabs>
  )
} 